import { DataGrid as MuiDataGrid, type DataGridProps as MuiDataGridProps } from '@mui/x-data-grid';
import { useMemo, useRef } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '../../theme/tokens';
import { Box } from './Box';
import { dataGridChromeSx } from './dataGridChrome';
import { getDataGridDefaults } from './dataGridDefaults';
import { DataGridPaginationFooter } from './DataGridPaginationFooter';
import {
  DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
  DataGridEmptyState,
  DataGridLoadingState,
} from './dataGridStates';
import { useTheme } from './hooks';
import type { Theme } from '@mui/material/styles';
import type { CSSProperties, ReactElement } from 'react';

export type DataGridProps = MuiDataGridProps & {
  emptyTitle?: string;
  emptyBody?: string;
  error?: boolean;
  errorTitle?: string;
  errorBody?: string;
  errorAction?: ReactElement;
  hidePaginationControls?: boolean;
  overlayHeight?: number | string;
  suppressFocusOutline?: boolean;
};

const useStableOptionalCallback = <TArgs extends readonly unknown[], TResult>(
  callback: ((...args: TArgs) => TResult) | undefined,
): ((...args: TArgs) => TResult) | undefined => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const hasCallback = callback !== undefined;

  return useMemo(() => {
    if (!hasCallback) return undefined;

    return (...args: TArgs): TResult => {
      const currentCallback = callbackRef.current;
      if (currentCallback === undefined) {
        throw new Error('Stable DataGrid callback invoked after being unset');
      }
      return currentCallback(...args);
    };
  }, [hasCallback]);
};

const dataGridFooterHeight = `${componentSizes.dataGrid.footerHeight}px`;

const DataGridFooter = (): ReactElement => (
  <Box
    className="MuiDataGrid-footerContainer MuiDataGrid-withBorderColor"
    sx={{
      alignItems: 'center',
      backgroundColor: 'background.paper',
      borderTop: (muiTheme: Theme) => `1px solid ${muiTheme.palette.divider}`,
      boxSizing: 'border-box',
      display: 'flex',
      flex: `0 0 ${dataGridFooterHeight}`,
      height: dataGridFooterHeight,
      justifyContent: 'flex-end',
      maxHeight: dataGridFooterHeight,
      minHeight: dataGridFooterHeight,
      overflow: 'hidden',
      width: '100%',
    }}
  >
    <DataGridPaginationFooter />
  </Box>
);

export const DataGrid = ({
  sx,
  style,
  rowHeight,
  columnHeaderHeight,
  getEstimatedRowHeight,
  getRowHeight,
  getRowSpacing,
  initialState,
  pageSizeOptions,
  slots,
  slotProps,
  disableColumnMenu,
  disableColumnSorting,
  emptyTitle,
  emptyBody,
  error,
  errorTitle,
  errorBody,
  errorAction,
  overlayHeight,
  suppressFocusOutline = false,
  ...rest
}: DataGridProps): ReactElement => {
  const theme = useTheme();
  const stableGetEstimatedRowHeight = useStableOptionalCallback(getEstimatedRowHeight);
  const stableGetRowHeight = useStableOptionalCallback(getRowHeight);
  const stableGetRowSpacing = useStableOptionalCallback(getRowSpacing);
  const resolvedDisableColumnMenu = disableColumnMenu ?? true;
  const resolvedDisableColumnSorting = disableColumnSorting ?? true;
  const defaults = getDataGridDefaults({
    emptyTitle,
    emptyBody,
    error,
    errorTitle,
    errorBody,
    errorAction,
  });
  const mergedInitialState = {
    ...defaults.initialState,
    ...initialState,
    pagination: {
      ...defaults.initialState.pagination,
      ...initialState?.pagination,
      paginationModel: {
        ...defaults.initialState.pagination.paginationModel,
        ...initialState?.pagination?.paginationModel,
      },
    },
    sorting: { ...defaults.initialState.sorting, ...initialState?.sorting },
  };
  const resolvedPageSizeOptions = pageSizeOptions ?? defaults.pageSizeOptions;
  const normalizeSxArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    return value === undefined || value === null ? [] : [value];
  };
  const defaultBasePopper = (defaults.slotProps as Record<string, unknown>).basePopper as
    | { sx?: unknown }
    | undefined;
  const providedBasePopper = (slotProps as Record<string, unknown> | undefined)?.basePopper as
    | { sx?: unknown }
    | undefined;
  const mergedSlotProps = {
    ...defaults.slotProps,
    ...slotProps,
    basePopper: {
      ...defaultBasePopper,
      ...providedBasePopper,
      sx: [
        {
          '& .MuiMenu-paper': {
            borderRadius: `${r('md')} !important`,
            overflow: 'hidden',
          },
          '& .MuiPaper-root': {
            borderRadius: `${r('md')} !important`,
          },
          '& .MuiMenuItem-root': {
            fontSize: '15px',
            lineHeight: 1.4,
          },
          '& .MuiMenuItem-root .MuiTypography-root, & .MuiMenuItem-root .MuiListItemText-primary': {
            fontSize: '15px',
            lineHeight: 1.4,
          },
        },
        ...normalizeSxArray(defaultBasePopper?.sx),
        ...normalizeSxArray(providedBasePopper?.sx),
      ],
    },
  } as MuiDataGridProps['slotProps'];

  const mergedSx = [
    dataGridChromeSx,
    {
      width: '100%',
      minWidth: 0,
      '--DataGrid-overlayHeight':
        typeof overlayHeight === 'number'
          ? `${overlayHeight}px`
          : (overlayHeight ?? `${componentSizes.dataGrid.stateMinHeight}px`),
      borderRadius: r('card'),
      border: 'none',
      backgroundColor: 'background.paper',
      '--DataGrid-t-color-background-base': (muiTheme: Theme) => muiTheme.palette.background.paper,
      '--DataGrid-t-color-background-overlay': (muiTheme: Theme) =>
        muiTheme.palette.background.paper,
      '--DataGrid-t-header-background-base': (muiTheme: Theme) => muiTheme.palette.background.paper,
      '--DataGrid-t-cell-background-pinned': (muiTheme: Theme) => muiTheme.palette.background.paper,
      '& .MuiDataGrid-mainContent, & .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-footerContainer, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner':
        {
          backgroundColor: 'background.paper',
        },
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: 'background.paper',
      },
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: 'background.paper',
        minWidth: 0,
        px: s('sm'),
      },
      '& .MuiDataGrid-main': { minWidth: 0 },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontFamily: (muiTheme: Theme) => muiTheme.typography.body1.fontFamily,
        fontSize: '15px',
        fontWeight: (muiTheme: Theme) => muiTheme.typography.body1.fontWeight,
        lineHeight: (muiTheme: Theme) => muiTheme.typography.body1.lineHeight,
      },
      '& .MuiDataGrid-cell': {
        fontFamily: (muiTheme: Theme) => muiTheme.typography.body.fontFamily,
        fontSize: '15px',
        fontWeight: (muiTheme: Theme) => muiTheme.typography.body.fontWeight,
        lineHeight: (muiTheme: Theme) => muiTheme.typography.body.lineHeight,
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        minWidth: 0,
        px: s('md'),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiDataGrid-cellContent': {
        fontSize: '15px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        alignItems: 'flex-start',
        paddingLeft: s('sm'),
        paddingRight: s('sm'),
      },
      '& .MuiDataGrid-cell .MuiTypography-root:not([data-grid-typography="custom"]), & .MuiDataGrid-cell .MuiLink-root:not([data-grid-typography="custom"])':
        {
          fontSize: '15px',
        },
      '& .MuiDataGrid-sortButton, & .MuiDataGrid-menuIconButton': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiDataGrid-sortButton:hover, & .MuiDataGrid-menuIconButton:hover': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiDataGrid-iconButtonContainer': { backgroundColor: 'transparent !important' },
      '& .MuiDataGrid-columnHeader:focus-visible, & .MuiDataGrid-cell:focus-visible': {
        outline: (muiTheme: Theme) => `2px solid ${muiTheme.app.state.focus}`,
        outlineOffset: 2,
      },
      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: 'transparent' },
      '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: 'transparent' },
    },
    suppressFocusOutline
      ? {
          '& .MuiDataGrid-cell:focus:not(:focus-visible), & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus:not(:focus-visible), & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
        }
      : null,
    sx,
  ].filter(Boolean) as MuiDataGridProps['sx'];

  const mergedStyle: CSSProperties = {
    ...style,
    ['--DataGrid-t-color-border-base' as string]: theme.palette.divider,
    ['--DataGrid-rowBorderColor' as string]: theme.palette.divider,
  };

  return (
    <MuiDataGrid
      autoHeight
      disableRowSelectionOnClick
      rowSelection={false}
      checkboxSelection={false}
      disableColumnMenu={resolvedDisableColumnMenu}
      disableColumnSorting={resolvedDisableColumnSorting}
      getEstimatedRowHeight={stableGetEstimatedRowHeight}
      getRowHeight={stableGetRowHeight}
      getRowSpacing={stableGetRowSpacing}
      rowHeight={rowHeight ?? theme.app.density.dataGrid.rowHeight}
      columnHeaderHeight={columnHeaderHeight ?? theme.app.density.dataGrid.headerHeight}
      pageSizeOptions={
        resolvedPageSizeOptions.length > 0 ? resolvedPageSizeOptions : defaults.pageSizeOptions
      }
      initialState={mergedInitialState}
      slots={{
        ...defaults.slots,
        footer: DataGridFooter,
        pagination: DataGridPaginationFooter,
        ...slots,
      }}
      slotProps={mergedSlotProps}
      style={mergedStyle}
      sx={mergedSx}
      {...rest}
    />
  );
};
export { DataGridEmptyState, DataGridLoadingState, DATA_GRID_EMPTY_STATE_NO_DATA_LABEL };
export { GridOverlay, type GridOverlayProps } from '@mui/x-data-grid';
